import { FC, ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
	Alert,
	AlertBody,
	AlertDescription,
	AlertIcon,
	AlertTitle,
	Avatar,
	BookmarkOutline,
	Box,
	Button,
	Center,
	ChatOutline,
	Display,
	Divider,
	DotsHorizontalOutline,
	Dropdown,
	DropdownInput,
	DropdownMenu,
	DropdownOption,
	Flex,
	FormControl,
	FormHelperText,
	Heading,
	Hide,
	HStack,
	IconButton,
	Image,
	Input,
	PlusCircleOutline,
	Radio,
	RadioGroup,
	rem,
	Show,
	SimpleGrid,
	Spacer,
	Stack,
	Text,
	Textarea,
	ThumbUpOutline,
	TrashOutline,
	useDisclosure,
} from '@bonusly/design-system';
import { useAnalytics } from '@bonusly/who-am-i';

import { Trophy } from '../../assets/components/trophy';
import { TextPreview } from '../../components/bonus/text-preview';
import { Conceal } from '../../components/display/conceal';
import { Reveal } from '../../components/display/reveal';
import { CountHelpText } from '../../components/form/count-help-text';
import { FormErrorMessage } from '../../components/form/form-error-message';
import { FormLabel } from '../../components/form/form-label';
import { FormSection } from '../../components/form/form-section';
import { InfoLabel } from '../../components/form/info-label';
import { RadioChip, RadioChipGroup } from '../../components/form/radio-chip';
import { UploadButton } from '../../components/form/upload-button';
import { ActionButton } from '../../components/recognition/bonus/components/bonus/action-button';
import { AwardVisibility, BudgetPeriod } from '../../entities/awards';
import { GroupType } from '../../entities/groups';
import { useCurrentUser } from '../../lib/who-am-i';
import { trackClick } from '../../utils/analytics';
import { generateFileURL } from '../../utils/file';
// TODO: Use shared compoent SHARK-2943
import { ApprovalProcess } from '../incentives/admin/approval-process';
import { AudienceSelector } from './audience-selector';
import { BudgetEstimateFixed } from './budget-estimate-fixed';
import { BudgetEstimateFlexible } from './budget-estimate-flexible';
import { EligibilityCount } from './eligibility-count';
import { AdminAwardTrackingEvent } from './events';
import { AwardFormData, BudgetMode, MessageMode } from './types';

type AwardFormProps = {
	children?: ReactNode;
	isLoading: boolean;
	form: ReturnType<typeof useForm<AwardFormData>>;
	onSubmit: (data: AwardFormData) => void;
};

export const AwardForm: FC<AwardFormProps> = ({ children, form, onSubmit }) => {
	const { t } = useTranslation('awards', { keyPrefix: 'form' });
	const preview = useDisclosure();
	const { track } = useAnalytics();
	const {
		control,
		formState: { errors },
		handleSubmit,
		register,
		watch,
	} = form;

	const [
		name,
		conditions,
		conditionsOperator,
		requireApproval,
		budgetMode,
		periodType,
		amount,
		budgetPerPeriod,
		icon,
		title,
		messageMode,
		message,
		visibility,
	] = watch([
		'name',
		'conditions',
		'conditionsOperator',
		'requireApproval',
		'budgetMode',
		'periodType',
		'amount',
		'budgetPerPeriod',
		'icon',
		'title',
		'messageMode',
		'message',
		'visibility',
	]);

	const iconURL = generateFileURL(icon);

	return (
		<Stack as="form" onSubmit={handleSubmit(onSubmit)} spacing={6}>
			<FormSection>
				<Heading size="xl">{t('basicSectionLabel')}</Heading>

				<FormControl isInvalid={!!errors.name}>
					<FormLabel>{t('nameLabel')}</FormLabel>
					<Input type="text" {...register('name')} />
					<CountHelpText count={name.length} error={errors.name?.message} max={40} />
				</FormControl>

				<FormControl isInvalid={!!errors.description}>
					<FormLabel isOptional>{t('descriptionLabel')}</FormLabel>
					<Textarea {...register('description')} data-testid="description-input" />
					<FormHelperText>{t('descriptionHelperText')}</FormHelperText>
				</FormControl>
			</FormSection>

			<FormSection>
				<Heading size="xl">{t('eligibilitySection')}</Heading>

				<FormControl isInvalid={!!errors.conditions}>
					<FormLabel>{t('audienceSectionLabel')}</FormLabel>
					<FormHelperText my={0} mb={2}>
						{t('audienceHelper')}
					</FormHelperText>

					<EligibilityCount conditions={conditions} operator={conditionsOperator} />

					<Controller
						control={control}
						name="conditionsOperator"
						render={({ field }) => (
							<RadioChipGroup onChange={field.onChange} value={field.value} mb={2}>
								<RadioChip value={GroupType.And}>{t('audienceAnd')}</RadioChip>
								<RadioChip value={GroupType.Or}>{t('audienceOr')}</RadioChip>
							</RadioChipGroup>
						)}
					/>

					<Controller
						control={control}
						name="conditions"
						render={({ field, formState }) => <AudienceSelector error={formState.errors.conditions?.message} onChange={field.onChange} value={field.value} />}
					/>
				</FormControl>
			</FormSection>

			<FormSection>
				<Heading size="xl">{t('pointsSection')}</Heading>

				<FormControl>
					<FormLabel mb={2}>{t('budgetModeLabel')}</FormLabel>

					<Controller
						control={control}
						name="budgetMode"
						render={({ field }) => (
							<RadioGroup
								value={field.value}
								onChange={(value) => {
									field.onChange(value);
									track(AdminAwardTrackingEvent.SetPointsBudgetMode, { budgetMode: value });
								}}
							>
								<Stack spacing={2}>
									<Radio value={BudgetMode.FlexibleLimit}>{t('budgetFlexibleLimit')}</Radio>
									<Radio value={BudgetMode.FixedLimit}>{t('budgetFixedLimit')}</Radio>
									<Radio value={BudgetMode.NoLimit}>{t('budgetNoLimit')}</Radio>
								</Stack>
							</RadioGroup>
						)}
					/>
				</FormControl>

				<Conceal if={budgetMode === BudgetMode.NoLimit}>
					<FormControl isInvalid={!!errors.budgetPerPeriod}>
						<FormLabel mb={2}>{t('budgetLabel')}</FormLabel>

						<SimpleGrid columns={2}>
							<Input {...register('budgetPerPeriod', { valueAsNumber: true })} flex={1} flexBasis="100%" borderEndRadius="none" borderEndColor="transparent" />
							<Controller
								control={control}
								name="periodType"
								render={({ field }) => (
									<Dropdown>
										<Dropdown onChange={field.onChange} renderOption={(value) => t('periodLabel', { context: value })} value={field.value}>
											<DropdownInput flex={1} flexBasis="100%" borderStartRadius="none" />
											<DropdownMenu>
												<DropdownOption value={BudgetPeriod.Week}>{t('periodLabel', { context: 'week' })}</DropdownOption>
												<DropdownOption value={BudgetPeriod.Month}>{t('periodLabel', { context: 'month' })}</DropdownOption>
												<DropdownOption value={BudgetPeriod.Quarter}>{t('periodLabel', { context: 'quarter' })}</DropdownOption>
												<DropdownOption value={BudgetPeriod.Year}>{t('periodLabel', { context: 'year' })}</DropdownOption>
												<DropdownOption value={BudgetPeriod.Lifetime}>{t('periodLabel', { context: 'lifetime' })}</DropdownOption>
											</DropdownMenu>
										</Dropdown>
									</Dropdown>
								)}
							/>
						</SimpleGrid>

						<FormErrorMessage>{errors.budgetPerPeriod?.message}</FormErrorMessage>
						<FormHelperText>{t('budgetHelperText', { context: periodType })}</FormHelperText>
					</FormControl>
				</Conceal>

				<Reveal if={budgetMode === BudgetMode.FixedLimit}>
					<FormControl isInvalid={!!errors.amount}>
						<InfoLabel label={t('amountTooltip')}>{t('amountLabel')}</InfoLabel>
						<Input {...register('amount', { valueAsNumber: true })} />
						<FormErrorMessage>{errors.amount?.message}</FormErrorMessage>
					</FormControl>

					<BudgetEstimateFixed amount={amount} budgetPerPeriod={budgetPerPeriod} period={periodType} />
				</Reveal>

				<Reveal if={budgetMode === BudgetMode.FlexibleLimit}>
					<BudgetEstimateFlexible budgetPerPeriod={budgetPerPeriod} period={periodType} />
				</Reveal>

				<Reveal if={budgetMode === BudgetMode.NoLimit}>
					<Alert status="info" size="sm">
						<AlertIcon />
						<AlertBody>
							<AlertTitle>{t('unlimitedTitle', { name, context: name ? 'named' : 'unnamed' })}</AlertTitle>
							<AlertDescription>{t('unlimitedDescription')}</AlertDescription>
						</AlertBody>
					</Alert>
				</Reveal>
			</FormSection>

			<FormSection>
				<Heading size="xl">{t('postsSection')}</Heading>

				<FormControl isInvalid={!!errors.icon}>
					<FormLabel>{t('iconLabel')}</FormLabel>
					<HStack spacing={5}>
						<Center
							boxSize={rem(80)}
							borderRadius="full"
							border="1px solid"
							borderColor={errors.icon ? 'error.inputBorder' : 'border.input'}
							bgColor="primary.bgAlpha"
							overflow="hidden"
						>
							{iconURL ? <Image src={iconURL} w="full" h="full" objectFit="cover" /> : <Trophy color="primary.fgStrong" pos="relative" left={0.5} top={0.5} />}
						</Center>

						<Flex flexDir="column" alignSelf="stretch" alignItems="flex-start">
							<Controller
								control={control}
								name="icon"
								render={({ field }) => {
									return (
										<HStack mb={2}>
											<UploadButton accept={['jpg', 'png']} onChange={field.onChange}>
												{t('uploadImageLabel')}
											</UploadButton>
											{iconURL && (
												<IconButton
													aria-label={t('deleteImageLabel')}
													variant="solid"
													colorScheme="gray"
													size="sm"
													isRound
													onClick={() => field.onChange(null)}
												>
													<TrashOutline size="sm" />
												</IconButton>
											)}
										</HStack>
									);
								}}
							/>
							<FormHelperText my={0}>{t('uploadImageHelperText')}</FormHelperText>
						</Flex>
					</HStack>
				</FormControl>

				<FormControl isInvalid={!!errors.title}>
					<FormLabel _disabled={{ color: 'gray.500' }}>{t('titleLabel')}</FormLabel>
					<Input type="text" {...register('title')} />
					<CountHelpText count={title.trim().length} max={40}>
						{t('titleHelperText')}
					</CountHelpText>
				</FormControl>

				<FormControl>
					<FormLabel mb={2}>{t('messageModeLabel')}</FormLabel>

					<Controller
						control={control}
						name="messageMode"
						render={({ field }) => (
							<RadioGroup value={field.value} onChange={field.onChange}>
								<Stack spacing={2}>
									<Radio value={MessageMode.Custom}>{t('messageModeCustom')}</Radio>
									<Radio value={MessageMode.Provided}>{t('messageModeProvided')}</Radio>
									<Radio value={MessageMode.Editable}>{t('messageModeEditable')}</Radio>
								</Stack>
							</RadioGroup>
						)}
					/>
				</FormControl>

				<Reveal if={messageMode !== MessageMode.Custom}>
					<FormControl isInvalid={!!errors.message}>
						<FormLabel>{t('messageLabel')}</FormLabel>
						<Textarea {...register('message')} />
						<FormErrorMessage>{errors.message?.message}</FormErrorMessage>
						<FormHelperText>{t('messageHelperText', { context: messageMode })}</FormHelperText>
					</FormControl>
				</Reveal>

				<FormControl isInvalid={!!errors.visibility}>
					<FormLabel>{t('visibilityLabel')}</FormLabel>
					<Controller
						control={control}
						name="visibility"
						render={({ field }) => (
							<Dropdown
								placement="bottom-start"
								onChange={field.onChange}
								value={field.value}
								valueToString={(value: string) => t('visibility', { context: value })}
							>
								<DropdownInput w="full" />

								<DropdownMenu>
									<DropdownOption value={AwardVisibility.Public}>{t('visibility_publicly_visible')}</DropdownOption>
									<DropdownOption value={AwardVisibility.Team}>{t('visibility_immediate_team')}</DropdownOption>
									<DropdownOption value={AwardVisibility.Private}>{t('visibility_privately_visible')}</DropdownOption>
								</DropdownMenu>
							</Dropdown>
						)}
					/>
					<FormHelperText>{t(`visibilityHelperText_${visibility}`)}</FormHelperText>
				</FormControl>

				<FormControl>
					<FormLabel>{t('visibilityEditableLabel')}</FormLabel>
					<Controller
						control={control}
						name="visibilityEditable"
						render={({ field }) => (
							<RadioGroup
								value={field.value.toString()}
								onChange={(value) => {
									field.onChange(value === 'true');
									track(AdminAwardTrackingEvent.ToggleVisibilityEditable, { visibilityEditable: value === 'true' });
								}}
							>
								<Stack spacing={2}>
									<Radio value="true">{t('visibilityEditableYes')}</Radio>
									<Radio value="false">{t('visibilityEditableNo')}</Radio>
								</Stack>
							</RadioGroup>
						)}
					/>
				</FormControl>

				<Reveal if={preview.isOpen}>
					<Divider />
					<Text size="sm">{t('previewDescription')}</Text>
					<FeedPreview amount={amount} budgetMode={budgetMode} iconURL={iconURL} message={message} messageMode={messageMode} title={title} />
				</Reveal>

				<HStack>
					<Button onClick={preview.onToggle} variant="ghost" {...trackClick(AdminAwardTrackingEvent.RecognitionPostPreview)}>
						{t('previewLabel', { context: preview.isOpen ? 'open' : 'closed' })}
					</Button>
				</HStack>
			</FormSection>

			<FormSection>
				<Heading size="xl">{t('approvalSection')}</Heading>

				<FormControl>
					<FormLabel>{t('approvalLabel')}</FormLabel>
					<Controller
						control={control}
						name="requireApproval"
						render={({ field }) => (
							<RadioGroup
								value={field.value.toString()}
								onChange={(value) => {
									field.onChange(value === 'true');
									track(AdminAwardTrackingEvent.SetApprovalProcess, { requireApproval: value === 'true' });
								}}
								mb={2}
							>
								<Stack spacing={2}>
									<Radio value="false">{t('approvalNotRequired')}</Radio>
									<Radio value="true">{t('approvalRequired')}</Radio>
								</Stack>
							</RadioGroup>
						)}
					/>
				</FormControl>

				<Reveal if={requireApproval}>
					<FormControl isInvalid={!!errors.approvalLevels}>
						<Controller
							control={control}
							name="approvalLevels"
							render={({ field }) => <ApprovalProcess error={errors.approvalLevels?.message} onChange={field.onChange} value={field.value} />}
						/>
					</FormControl>
				</Reveal>
			</FormSection>

			{children}
		</Stack>
	);
};

type FeedPreviewProps = {
	amount: number | null;
	budgetMode: BudgetMode;
	iconURL: string | null;
	message: string;
	messageMode: MessageMode;
	title: string;
};

export const FeedPreview: FC<FeedPreviewProps> = ({ amount, budgetMode, iconURL, message, messageMode, title }) => {
	const user = useCurrentUser();
	const { t } = useTranslation('awards', { keyPrefix: 'form' });

	let displayAmount = 10;

	if (typeof amount === 'number' && amount && budgetMode === BudgetMode.FixedLimit) {
		displayAmount = amount;
	}

	let displayMessage = `+${displayAmount} @${user.username} `;

	switch (messageMode) {
		case MessageMode.Custom:
			displayMessage += t('previewDefaultMessage');
			break;
		case MessageMode.Provided:
			displayMessage += message;
			break;
		case MessageMode.Editable:
			displayMessage += `${message.trim()} ${t('previewDefaultMessage')}`;
			break;
	}

	return (
		<Box bgColor="secondary.bgAlpha" border="1px solid #6B74853D" borderRadius="md" p={5}>
			<Box bgColor="white" borderRadius="lg" mb={4} border="1px solid" borderColor="border.default" margin="auto" maxW={rem(522)}>
				{/* Header */}
				<HStack p={4} columnGap={2} borderBottom="1px solid" borderColor="border.default">
					{iconURL && <Image src={iconURL} boxSize={rem(38)} borderRadius="full" />}
					{!iconURL && (
						<Box boxSize={rem(38)} borderRadius="full" overflow="hidden">
							<Trophy w="full" h="full" />
						</Box>
					)}
					<Center bgColor="points.bgAlpha" h={rem(38)} borderRadius="full" color="points.fg" fontWeight="semibold" px={2.5} fontSize="lg">
						+{Math.trunc(Math.abs(displayAmount))}
					</Center>
					<Spacer />
					<Text size="sm" color="gray.700">
						{t('previewTimeStamp')}
					</Text>
					<BookmarkOutline color="secondary.fg" />
					<DotsHorizontalOutline color="secondary.fg" />
				</HStack>

				{/* Body */}
				<Flex flexDir={{ base: 'column-reverse', sm: 'row' }}>
					<Stack p={4} minH={{ base: 0, sm: rem(230) }} w="full">
						<HStack w="full">
							<Display mb={2} size="xs">
								{title.trim() || t('previewDefaultTitle')}
							</Display>
							<Spacer />
							<Avatar src={user.profile_pic_url} name={user.display_name} size="xl" />
						</HStack>
						<TextPreview text={displayMessage}>
							<Box as="span" fontWeight="semibold">
								{t('previewGiverName')}:{' '}
							</Box>
						</TextPreview>
					</Stack>
					<Spacer />
				</Flex>

				{/* Footer */}
				<HStack px={5} py={4} spacing={2} borderTop="1px solid" borderColor="border.default">
					<ActionButton icon={<ChatOutline />} color="gray">
						{t('previewComment')}
					</ActionButton>
					<Hide below="xs">
						<ActionButton icon={<PlusCircleOutline />} color="green">
							{t('previewAddOn')}
						</ActionButton>
					</Hide>
					<Show below="xs">
						<IconButton aria-label="Like" bg="white" border="1px solid" borderColor="gray.100" _hover={{ bg: 'grayAlpha.100' }}>
							<DotsHorizontalOutline />
						</IconButton>
					</Show>
					<Spacer />

					<Hide below="xs">
						<ActionButton icon={<ThumbUpOutline />} color="blue">
							{t('previewLike')}
						</ActionButton>
					</Hide>
					<Show below="xs">
						<IconButton aria-label="Like" bg="white" border="1px solid" borderColor="gray.100" _hover={{ bg: 'blueAlpha.100' }}>
							<ThumbUpOutline />
						</IconButton>
					</Show>
				</HStack>
			</Box>
		</Box>
	);
};
